import { useRouter } from 'next/router';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import { HELPER_NAV_SCROLL_THRESHOLD } from '@/components/GlobalNavigation/DesktopNav/DesktopNav';
import { NavPosition } from '@/components/GlobalNavigation/GlobalNavigation';

type RestingPosition = NavPosition.STATIC | NavPosition.ABSOLUTE;

const useNavPosition = (
  immersive: boolean | undefined,
  scrollPosition: number,
  scrollDirection: string
): [NavPosition, Dispatch<SetStateAction<NavPosition>>] => {
  const isMounted = useRef(false);

  const [restingPosition, setRestingPosition] = useState<RestingPosition>(
    immersive ? NavPosition.ABSOLUTE : NavPosition.STATIC
  );
  // Initialize to the resting position so the very first (SSR) render already
  // matches the final layout. Defaulting to STATIC on immersive pages put the
  // nav in-flow on first paint and then flipped it to ABSOLUTE after mount,
  // shifting <main> up by the nav height (a large CLS contributor).
  const [navPosition, setNavPosition] = useState<NavPosition>(
    immersive ? NavPosition.ABSOLUTE : NavPosition.STATIC
  );

  const router = useRouter();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    setRestingPosition(NavPosition.STATIC);
    setNavPosition(NavPosition.STATIC);
  }, [router?.asPath]);

  //sent initial position and transparency
  useEffect(() => {
    if (immersive) {
      setRestingPosition(NavPosition.ABSOLUTE);
      setNavPosition(NavPosition.ABSOLUTE);
    }
  }, [immersive]);

  // Set nav position after initial mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    if (isMounted.current) {
      // Set position as fixed at the bottom of the page
      if (
        scrollDirection === 'up' &&
        scrollPosition >= HELPER_NAV_SCROLL_THRESHOLD
      ) {
        setNavPosition(NavPosition.FIXED);
      }

      // Set resting position once at the top of the page
      if (scrollDirection === 'up' && scrollPosition <= 5) {
        setNavPosition(restingPosition);
      }
    } else {
      isMounted.current = true;
    }
  }, [scrollPosition, scrollDirection]);

  return [navPosition, setNavPosition];
};

export default useNavPosition;
